
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface BatchedAvailabilityResult {
  [propertyId: string]: {
    unavailableDates: Date[];
    isLoading: boolean;
    error: string | null;
    refetch?: () => void;
  };
}

export const useBatchedAvailability = (propertyIds: string[]): BatchedAvailabilityResult => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['batched-availability', propertyIds.sort()],
    queryFn: async (): Promise<{ [propertyId: string]: Date[] }> => {
      console.log('🔄 Fetching batched availability for properties:', propertyIds);
      
      if (propertyIds.length === 0) {
        return {};
      }
      
      // Obtener fechas bloqueadas manualmente
      const { data: blockedDaysData, error: blockedError } = await supabase
        .from('property_blocked_days')
        .select('property_id, blocked_date')
        .in('property_id', propertyIds)
        .order('blocked_date', { ascending: true });

      if (blockedError) {
        console.error('Error fetching batched blocked days:', blockedError);
        throw blockedError;
      }

      // Obtener reservaciones activas - solo seleccionar campos necesarios para evitar errores con los nuevos campos
      const { data: bookingsData, error: bookingsError } = await supabase
        .from('bookings')
        .select('property_id, check_in, check_out, status')
        .in('property_id', propertyIds)
        .eq('status', 'Reservada')
        .order('check_in', { ascending: true });

      if (bookingsError) {
        console.error('Error fetching batched bookings:', bookingsError);
        throw bookingsError;
      }

      // Inicializar resultado
      const result: { [propertyId: string]: Date[] } = {};
      propertyIds.forEach(id => {
        result[id] = [];
      });

      // Procesar fechas bloqueadas manualmente
      blockedDaysData?.forEach(item => {
        const dateString = item.blocked_date;
        console.log(`🔍 Processing batched blocked date: ${dateString} for property ${item.property_id}`);
        
        const [year, month, day] = dateString.split('-').map(Number);
        const localDate = new Date(year, month - 1, day);
        
        if (!result[item.property_id]) {
          result[item.property_id] = [];
        }
        
        result[item.property_id].push(localDate);
      });

      // Procesar fechas bloqueadas por reservaciones
      bookingsData?.forEach(booking => {
        const checkIn = new Date(booking.check_in);
        const checkOut = new Date(booking.check_out);
        
        console.log(`🔍 Processing batched booking for ${booking.property_id}: ${booking.check_in} to ${booking.check_out}`);
        
        // Bloquear todas las noches desde check-in hasta check-out (excluyendo check-out)
        const currentDate = new Date(checkIn);
        while (currentDate < checkOut) {
          if (!result[booking.property_id]) {
            result[booking.property_id] = [];
          }
          result[booking.property_id].push(new Date(currentDate));
          currentDate.setDate(currentDate.getDate() + 1);
        }
      });

      console.log('✅ Batched availability fetch complete:', Object.keys(result).map(id => 
        `${id}: ${result[id].length} dates`
      ));
      
      return result;
    },
    enabled: propertyIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Transform data into the expected format
  const result: BatchedAvailabilityResult = {};
  
  propertyIds.forEach(propertyId => {
    result[propertyId] = {
      unavailableDates: data?.[propertyId] || [],
      isLoading,
      error: error ? String(error) : null,
      refetch
    };
  });

  return result;
};
